import { useFollowCategoryMutation } from "~/queries/catalog/categoryPreferences";
import { useFollowEventMutation } from "~/queries/catalog/eventPreferences";
import { useFollowProductMutation } from "~/queries/catalog/productPreferences";

export enum FollowFeatureType {
  Product = "product",
  Category = "category",
  Event = "event",
}

const FOLLOW_MUTATON_MAP = {
  [FollowFeatureType.Product]: useFollowProductMutation,
  [FollowFeatureType.Category]: useFollowCategoryMutation,
  [FollowFeatureType.Event]: useFollowEventMutation,
} as const;

type FollowMutation<T extends FollowFeatureType> =
  (typeof FOLLOW_MUTATON_MAP)[T];

export type FollowMutationFnParams<T extends FollowFeatureType> = Parameters<
  ReturnType<FollowMutation<T>>["mutateAsync"]
>["0"];

export const useFollow = <T extends FollowFeatureType>({
  type,
}: {
  type: T;
}) => {
  const { isLoggedIn } = useAuth();

  const { mutate: followMutate, status: followStatus } =
    FOLLOW_MUTATON_MAP[type]();

  const onToggleFollow = (options: FollowMutationFnParams<T>) => {
    if (!isLoggedIn.value) return;

    if (followStatus.value === "pending") return;

    followMutate({
      id: options.id,
      newFollowStatus: options.newFollowStatus,
      trackData: options.trackData as never, // as never is a workaround for a type union issue. see: https://www.totaltypescript.com/as-never
    });
  };

  return {
    onToggleFollow,
    followStatus,
  };
};
